import React from "react";
import type { Environment } from "components/Environment/utils/types";
import { Button } from "components/Graphs/Button";
import { FullScreenLoadingSpinner } from "components/FullScreenLoader";
import ApplicationTableRow from "./ApplicationTableRow";
import { useApplicationGroups } from "./hooks/useApplicationGroups";

const ApplicationsPanel: React.FC<{
  activeOrgId: string;
  environments: Environment[];
  isError: boolean;
  isLoading: boolean;
  onOpenEnvironment: (path: string) => void;
  onRetry: () => void;
  search: string;
}> = ({
  activeOrgId,
  environments,
  isError,
  isLoading,
  onOpenEnvironment,
  onRetry,
  search,
}) => {
  const applications = useApplicationGroups(environments, search);

  if (isLoading) {
    return (
      <section
        id="applications-panel"
        role="tabpanel"
        aria-labelledby="applications-tab"
        className="py-4"
      >
        <FullScreenLoadingSpinner heightClassName="h-32" />
      </section>
    );
  }

  if (isError) {
    return (
      <section
        id="applications-panel"
        role="tabpanel"
        aria-labelledby="applications-tab"
        className="rounded-lg border border-zinc-800 bg-zinc-900/30 py-8 text-center"
      >
        <h2 className="text-sm font-semibold text-zinc-100">
          Applications could not be loaded
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">
          There was an issue loading applications for this organization.
        </p>
        <Button variant="secondary" className="mt-5" onClick={onRetry}>
          Retry
        </Button>
      </section>
    );
  }

  return (
    <section
      id="applications-panel"
      role="tabpanel"
      aria-labelledby="applications-tab"
      className="py-4"
    >
      {applications.all.length ? (
        <div className="overflow-x-auto rounded-lg border border-[#1e1e1e]">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead className="bg-black/[.18]">
              <tr className="border-b border-[#1e1e1e]">
                {["Name", "Link", "Compute", "Created", "Owner"].map(
                  (label) => (
                    <th
                      key={label}
                      className="whitespace-nowrap px-4 py-[7px] text-[9.5px] font-bold uppercase tracking-[.08em] text-[#707070]"
                    >
                      {label}
                    </th>
                  ),
                )}
                <th className="px-4 py-[7px]" aria-label="Status" />
              </tr>
            </thead>
            <tbody>
              {applications.active.map((item, index) => (
                <ApplicationTableRow
                  key={`${item.environment.workspaceId}-${item.application.hostname}-${index}`}
                  item={item}
                  state="active"
                  activeOrgId={activeOrgId}
                  onOpenEnvironment={onOpenEnvironment}
                />
              ))}
              {applications.inaccessible.map((item, index) => (
                <ApplicationTableRow
                  key={`${item.environment.workspaceId}-${item.application.hostname}-${index}`}
                  item={item}
                  state="inaccessible"
                  sectionLabel={index === 0 ? "No access" : undefined}
                  activeOrgId={activeOrgId}
                  onOpenEnvironment={onOpenEnvironment}
                />
              ))}
              {applications.inactive.map((item, index) => (
                <ApplicationTableRow
                  key={`${item.environment.workspaceId}-${item.application.hostname}-${index}`}
                  item={item}
                  state="inactive"
                  sectionLabel={index === 0 ? "Stopped compute" : undefined}
                  activeOrgId={activeOrgId}
                  onOpenEnvironment={onOpenEnvironment}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-5 py-12 text-center text-[13px] text-[#707070]">
          <p className="text-sm text-secondary">No applications</p>
          <p className="mt-2 text-sm text-[#707070]">
            {search.trim()
              ? "No applications match the current filters."
              : "Expose an application on compute to see it here."}
          </p>
        </div>
      )}
    </section>
  );
};

export default ApplicationsPanel;
